import { KeyboardEvent, useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/common/modal";
import { Button } from "components/common/buttons/Button";
import { InputV3 } from "components/common/input/InputV3";
import { useBudgetScenarioComparison } from "hooks/useBudget";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { helpArticleUrls } from "config/helpArticleUrls";
import { ApiErrorCode } from "config";
import { BudgetVersionDiffPanel } from "../BudgetVersionDiffPanel";
import { BudgetScenarioCompareModalProps } from "./BudgetScenarioCompareModal.types";
import { buildComparisonRows } from "./BudgetScenarioCompareModal.utils";
import { ComparisonTable } from "./components/comparison-table";
import { VersionList } from "./components/version-list";

export const BudgetScenarioCompareModal = ({
  open,
  onClose,
  currencySymbol,
}: BudgetScenarioCompareModalProps) => {
  const { projectId, documentId } = useProjectIdentifiers();
  const [diffVersion, setDiffVersion] = useState<{ id: number; label: string } | null>(null);
  const {
    scenarioName,
    setScenarioName,
    duplicateName,
    canSave,
    isSaving,
    loadingVersionId,
    loadedVersionIds,
    scenarios,
    hasBudget,
    versionsQuery,
    liveVersionId,
    saveScenario,
    saveErrorCode,
    toggleVersion,
    removeScenario,
    clearScenarios,
    makeVersionLive,
    deleteVersion,
    restoreVersion,
    isViewer,
    versionQuota,
  } = useBudgetScenarioComparison({ open });

  const rows = useMemo(() => buildComparisonRows(scenarios), [scenarios]);
  const pinnedCount = rows.filter((row) => !row.isBaseline).length;
  const quotaLabel =
    versionQuota != null ? `${versionQuota.used} / ${versionQuota.max} saved scenarios` : null;

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && canSave && !isViewer) {
      event.preventDefault();
      saveScenario();
    }
  };

  return (
    <>
      <Modal isOpen={open} onClose={onClose} maxWidth="max-w-2xl">
        <ModalHeader title="Compare budget scenarios" onClose={onClose} />
        <ModalBody>
          <div className="flex flex-col gap-5 text-left">
            <p className="text-left text-sm leading-relaxed text-muted-foreground">
              Save the current estimate as a named scenario, then pin saved versions to compare
              totals against your live budget (
              <span className="text-foreground/80">Difference vs live</span>
              ). Category and line-item diffs open from each saved version.{" "}
              <a
                href={helpArticleUrls.budgetScenarios}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-secondary underline-offset-2 hover:underline"
              >
                Learn more
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </p>

            {!isViewer ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <label
                    htmlFor="scenario-name"
                    className="text-left text-[13px] font-medium text-foreground/80"
                  >
                    Save current budget as a scenario
                  </label>
                  {quotaLabel ? (
                    <span className="text-[11px] text-muted-foreground/80">{quotaLabel}</span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <InputV3
                      id="scenario-name"
                      value={scenarioName}
                      onChange={(event) => setScenarioName(event.target.value)}
                      onKeyDown={handleNameKeyDown}
                      placeholder="Scenario name (e.g. 25 shoot days)"
                      aria-label="Scenario name"
                      error={
                        duplicateName || saveErrorCode === ApiErrorCode.BUDGET_VERSION_LIMIT_REACHED
                      }
                    />
                  </div>
                  <Button
                    variant="ghostBrand"
                    disabled={!canSave}
                    isLoading={isSaving}
                    onClick={saveScenario}
                  >
                    Save scenario
                  </Button>
                </div>
                {duplicateName ? (
                  <p className="text-left text-xs text-danger">
                    A version with this name already exists. Choose a different name.
                  </p>
                ) : null}
                {saveErrorCode === ApiErrorCode.BUDGET_VERSION_LIMIT_REACHED ? (
                  <p className="text-left text-xs text-danger">
                    Saved scenario limit reached
                    {versionQuota != null ? ` (${versionQuota.used}/${versionQuota.max})` : ""}.
                    Delete an unused scenario or upgrade your plan.
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-left text-[13px] text-muted-foreground">
                You can compare scenarios. Saving, restoring, and making live require Editor access.
                {quotaLabel ? ` ${quotaLabel}.` : ""}
              </p>
            )}

            <div className="h-px bg-muted" />

            <div className="flex flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <span className="text-left text-[13px] font-medium text-foreground/80">
                  Saved versions
                </span>
                {pinnedCount > 0 ? (
                  <span className="text-[11px] text-muted-foreground/70">{pinnedCount} pinned</span>
                ) : null}
              </div>
              <VersionList
                versions={versionsQuery.data ?? []}
                liveVersionId={liveVersionId}
                // Not `isLoading`: without a document the request never runs, and a disabled
                // React Query v4 query stays in status "loading" forever.
                isLoading={versionsQuery.isInitialLoading}
                isError={versionsQuery.isError}
                loadedVersionIds={loadedVersionIds}
                loadingVersionId={loadingVersionId}
                onToggle={toggleVersion}
                onRestore={restoreVersion}
                onDelete={deleteVersion}
                onMakeLive={makeVersionLive}
                onCompareDiff={(version) =>
                  setDiffVersion({
                    id: version.budget_id,
                    label: version.version_name?.trim() || `Version ${version.budget_id}`,
                  })
                }
                isViewer={isViewer}
              />
            </div>

            <div className="flex flex-col gap-2.5">
              <span className="text-left text-[13px] font-medium text-foreground/80">
                Comparison
              </span>
              {hasBudget ? (
                <ComparisonTable
                  rows={rows}
                  currencySymbol={currencySymbol}
                  onRemove={removeScenario}
                />
              ) : (
                <p className="text-left text-[13px] text-muted-foreground">
                  Current budget isn’t available yet.
                </p>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="pt-2">
          {pinnedCount > 0 ? (
            <Button variant="ghostSecondary" size="sm" onClick={clearScenarios} className="mr-auto">
              Clear comparison
            </Button>
          ) : null}
          <Button variant="ghostSecondary" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
      {diffVersion && projectId && documentId ? (
        <BudgetVersionDiffPanel
          open={Boolean(diffVersion)}
          onClose={() => setDiffVersion(null)}
          projectId={projectId}
          documentId={documentId}
          compareBudgetId={diffVersion.id}
          againstBudgetId={liveVersionId}
          compareLabel={diffVersion.label}
          currencySymbol={currencySymbol}
        />
      ) : null}
    </>
  );
};
